/**
 * ContactSection Component
 *
 * Displays contact information and form for showroom inquiries.
 * Three-column layout: Title | Info | Form
 * All form fields are mandatory with standard validation.
 */

import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useIntl} from 'react-intl';
import {useForm} from 'react-hook-form';

import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Link,
  Text,
  Textarea,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {PhoneIcon, EmailIcon, ProfileIcon} from '@salesforce/retail-react-app/app/components/icons';

import {toSfccLocale} from '@amblique/king-living/app/utils/utils';
import {
  FIELD_LIMITS,
  validateEmail,
  validateMaxLength,
  validateName,
} from '@amblique/king-living/app/utils/validation';
import {appendRequiredAsterisk} from '@amblique/king-living/app/components/field/field-label-content';
import {useRecaptcha} from '@amblique/king-living/app/hooks/use-recaptcha';
import {
  RecaptchaDisclosure,
  RecaptchaErrorMessage,
  withRecaptcha,
} from '@amblique/king-living/app/components/recaptcha';
import {RECAPTCHA_ACTIONS} from '@amblique/king-living/app/constants/recaptcha';

/**
 * ContactSection component
 *
 * @param {Object} props - Component props
 * @param {Object} props.store - SFCC Store data
 * @param {Object} props.contactFormDescription - Rich text description from Contentful
 * @returns {React.ReactElement}
 */
const ContactSectionBase = ({
  contactFormDescription,
  store,
  ...props
}) => {
  const {formatMessage, locale: intlLocale} = useIntl();
  const styles = useMultiStyleConfig('ContactSection', {});

  // reCAPTCHA v3 token verification (GR-004/005/012) — Contact Us scope item.
  const {verify: verifyRecaptcha} = useRecaptcha();

  const [submitStatus, setSubmitStatus] = useState('idle'); // 'idle' | 'success' | 'error'
  const [submitErrorMessage, setSubmitErrorMessage] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting},
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      message: '',
    },
  });

  const emailPlaceholder = formatMessage({
    defaultMessage: 'Email address',
    id: 'contact_section.email_placeholder',
  });
  const firstNameLabel = formatMessage({
    defaultMessage: 'First name',
    id: 'contact_section.first_name_placeholder',
  });
  const lastNameLabel = formatMessage({
    defaultMessage: 'Last name',
    id: 'contact_section.last_name_placeholder',
  });
  const messagePlaceholder = formatMessage({
    defaultMessage: 'Message',
    id: 'contact_section.message_placeholder',
  });
  const messageLabel = formatMessage({
    defaultMessage: 'Message',
    id: 'contact_section.message_placeholder',
  });
  const sendEnquiryLabel = formatMessage({
    defaultMessage: 'Send enquiry',
    id: 'contact_section.send_enquiry',
  });
  const sendingLabel = formatMessage({
    defaultMessage: 'Sending…',
    id: 'contact_section.sending',
  });

  const onSubmit = async (data) => {
    setSubmitStatus('idle');
    setSubmitErrorMessage(null);
    try {
      // GR-012: verify reCAPTCHA before sending the enquiry. On a failed score
      // the request is blocked and the GR-012 message is shown below the form.
      const recaptcha = await verifyRecaptcha(RECAPTCHA_ACTIONS.CONTACT);

      if (!recaptcha.success) {
        setSubmitErrorMessage(recaptcha.message);
        setSubmitStatus('error');

        return;
      }

      const toEmail = store?.email;

      if (!toEmail) {
        setSubmitStatus('error');

        return;
      }
      const localeParam = toSfccLocale(intlLocale);
      const body = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        message: data.message,
        toEmail,
      };

      if (localeParam) body.locale = localeParam;

      const res = await fetch('/server/showroom-enquiry', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));

        throw new Error(json.message || 'Failed to send enquiry');
      }

      setSubmitStatus('success');
      reset();
    } catch (err) {
      setSubmitStatus('error');
    }
  };

  // Get manager name from store custom attribute
  const managerName = store?.showroomManager || store?.c_showroomManager;

  // Extract plain text from rich text JSON if available
  const descriptionText = contactFormDescription?.json?.content
    ?.map((node) => node.content?.map((c) => c.value).join(''))
    .join(' ') || '';

  return (
    <Box {...styles.container} {...props}>
      <Grid {...styles.grid}>
        {/* Column 1: Title */}
        <GridItem {...styles.titleColumn}>
          <Heading as="h2" {...styles.title}>
            {formatMessage({
              defaultMessage: 'Contact showroom',
              id: 'contact_section.title',
            })}
          </Heading>
        </GridItem>

        {/* Column 2: Contact Info */}
        <GridItem {...styles.infoColumn}>
          <VStack {...styles.infoStack}>
            {/* Phone */}
            {store?.phone && (
              <HStack {...styles.contactItem}>
                <PhoneIcon {...styles.icon} />
                <Link
                  {...styles.link}
                  href={`tel:${store.phone}`}
                >
                  {store.phone}
                </Link>
              </HStack>
            )}

            {/* Email */}
            {store?.email && (
              <HStack {...styles.contactItem}>
                <EmailIcon {...styles.icon} />
                <Link
                  {...styles.link}
                  href={`mailto:${store.email}`}
                >
                  {store.email}
                </Link>
              </HStack>
            )}

            {/* Showroom Manager */}
            {managerName && (
              <HStack {...styles.contactItem}>
                <ProfileIcon {...styles.icon} />
                <Text {...styles.text}>
                  {managerName} - {formatMessage({
                    defaultMessage: 'Showroom manager',
                    id: 'contact_section.showroom_manager',
                  })}
                </Text>
              </HStack>
            )}

            {/* Description */}
            {descriptionText && (
              <Text {...styles.description}>
                {descriptionText}
              </Text>
            )}
          </VStack>
        </GridItem>

        {/* Column 3: Contact Form or thank you / error message */}
        <GridItem {...styles.formColumn}>
          {submitStatus === 'success' && (
            <Text {...styles.description}>
              {formatMessage({
                defaultMessage: 'Thank you for your enquiry. We’ll get back to you soon.',
                id: 'contact_section.thank_you',
              })}
            </Text>
          )}
          {submitStatus === 'error' && (
            <RecaptchaErrorMessage
              message={
                submitErrorMessage ||
                formatMessage({
                  defaultMessage: 'Something went wrong. Please try again.',
                  id: 'contact_section.error_message',
                })
              }
              mb={3}
            />
          )}
          {(submitStatus === 'idle' || submitStatus === 'error') && (
            <form noValidate {...styles.form} onSubmit={handleSubmit(onSubmit)}>
              <VStack {...styles.formStack}>
                <FormControl isInvalid={!!errors.firstName}>
                  <Input
                    {...styles.input}
                    aria-label={appendRequiredAsterisk(firstNameLabel)}
                    aria-required="true"
                    maxLength={FIELD_LIMITS.firstName}
                    placeholder={appendRequiredAsterisk(firstNameLabel)}
                    {...register('firstName', {
                      required: formatMessage({
                        defaultMessage: 'First name is required',
                        id: 'contact_section.validation.first_name_required',
                      }),
                      validate: (value) => validateName(value, {
                        maxLength: FIELD_LIMITS.firstName,
                        fieldName: firstNameLabel,
                        formatMessage,
                      }),
                    })}
                  />
                  <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.lastName}>
                  <Input
                    {...styles.input}
                    aria-label={appendRequiredAsterisk(lastNameLabel)}
                    aria-required="true"
                    maxLength={FIELD_LIMITS.lastName}
                    placeholder={appendRequiredAsterisk(lastNameLabel)}
                    {...register('lastName', {
                      required: formatMessage({
                        defaultMessage: 'Last name is required',
                        id: 'contact_section.validation.last_name_required',
                      }),
                      validate: (value) => validateName(value, {
                        maxLength: FIELD_LIMITS.lastName,
                        fieldName: lastNameLabel,
                        formatMessage,
                      }),
                    })}
                  />
                  <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.email}>
                  <Input
                    {...styles.input}
                    aria-label={appendRequiredAsterisk(emailPlaceholder)}
                    aria-required="true"
                    placeholder={appendRequiredAsterisk(emailPlaceholder)}
                    type="email"
                    {...register('email', {
                      required: formatMessage({
                        defaultMessage: 'Email address is required',
                        id: 'contact_section.validation.email_required',
                      }),
                      validate: (value) => validateEmail(value, {
                        maxLength: FIELD_LIMITS.email,
                        formatMessage,
                      }),
                    })}
                  />
                  <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.message}>
                  <Textarea
                    {...styles.textarea}
                    aria-label={appendRequiredAsterisk(messageLabel)}
                    aria-required="true"
                    maxLength={FIELD_LIMITS.message}
                    placeholder={appendRequiredAsterisk(messagePlaceholder)}
                    {...register('message', {
                      required: formatMessage({
                        defaultMessage: 'Message is required',
                        id: 'contact_section.validation.message_required',
                      }),
                      validate: (value) => validateMaxLength(value, {
                        maxLength: FIELD_LIMITS.message,
                        fieldName: messageLabel,
                        formatMessage,
                      }),
                    })}
                  />
                  <FormErrorMessage>{errors.message?.message}</FormErrorMessage>
                </FormControl>

                <Button
                  {...styles.submitButton}
                  isLoading={isSubmitting}
                  loadingText={sendingLabel}
                  type="submit"
                >
                  {sendEnquiryLabel}
                </Button>

                {/* KL-1046 — required Google disclosure (reCAPTCHA badge is hidden globally). */}
                <RecaptchaDisclosure />
              </VStack>
            </form>
          )}
        </GridItem>
      </Grid>
    </Box>
  );
};

ContactSectionBase.displayName = 'ContactSection';

ContactSectionBase.propTypes = {
  contactFormDescription: PropTypes.shape({
    json: PropTypes.object,
  }),
  store: PropTypes.shape({
    c_showroomManager: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    showroomManager: PropTypes.string,
  }),
};

// Wrap in the reCAPTCHA provider so `useRecaptcha` can mint/verify tokens on the
// showroom detail page. Named export is what consumers (showroom-details model,
// showroom/index) import, so the provider mounts wherever the form renders.
export const ContactSection = withRecaptcha(ContactSectionBase);

export default ContactSection;
